import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BuscaGlobal } from './BuscaGlobal';
import { Relatar } from './Relatar';
import { plural, rotulo } from './formato';
import {
  ESPACOS, ETIQUETA, EspacoDeTrabalho, MODULOS, Modulo, alcanca, descricaoDoEspaco,
  espacoDaTela, espacosDaFaixa, espacosDaSessao, itensDaEngrenagem, menuPorGrupo,
  nomeDoEspaco, recusaDaTela, rotaDoEspaco,
} from './modulos';
import { enderecoDoModulo, estaAqui, faixaPrometeFila, iniciais } from './regrasDaMoldura';
import { FolhaDoMais } from './FolhaDoMais';
import { ContaSemAcesso, RecusaDoEspaco } from './SemAcesso';
import { useSessao } from './sessao';
import { EscolherPersona, FaixaDaPersona } from './Persona';
import { NOME_TEMA, Tema, aplicarTema, lerTema, proximoTema } from './tema';
import { aoMudarConexao, estadoDaConexao, type EstadoDaConexao } from './api';
import { aoMudarFila, tamanhoDaFila } from './fila';
import { FilaDeEnvio } from './FilaDeEnvio';
import { Caminho, Elo, ProvedorDoCaminho } from './Caminho';
import { Icone, iconeDoModulo } from './Icone';
import { useEstreito } from './ui';
import { useContagemDaEstufa } from './contagemDaEstufa';
// Onda 548 (Plano 2, Tarefa 4): o sino dos avisos.
import { SinoDeAvisos } from './SinoDeAvisos';

/*
 * As três palavras do chip de conexão.
 *
 * Cada uma diz o que fazer, e não só o que houve — «sem conexão» sozinho
 * deixa o técnico decidir se para de digitar ou não, e o que ele precisa
 * saber é que o que já foi lançado está gravado.
 */
const ROTULO_DA_CONEXAO: Record<EstadoDaConexao, string> = {
  no_ar: '● Conectado',
  tentando: '◐ Reenviando',
  sem_rede: '▲ Sem conexão',
};

const TOM_DA_CONEXAO: Record<EstadoDaConexao, string> = {
  no_ar: 'ok', tentando: 'atencao', sem_rede: 'alerta',
};

const RECADO_DA_CONEXAO: Record<EstadoDaConexao, string> = {
  no_ar: 'A última chamada foi e voltou.',
  tentando: 'A rede falhou e o envio está sendo repetido. Repetir é '
    + 'seguro: cada lançamento leva uma chave, e o servidor não grava o '
    + 'mesmo duas vezes.',
  sem_rede: 'A última chamada não chegou ao servidor. O que já foi '
    + 'gravado está gravado; o que você digitar agora só sai quando a '
    + 'rede voltar.',
};

/**
 * A moldura da aplicação — menu lateral, cabeçalho, conteúdo.
 *
 * É o desenho do protótipo validado (`claude/05-ux-e-prototipo.md`), e a
 * volta a ele é uma correção: enquanto existia UMA tela, subi uma barra no
 * topo com dois links, porque menu lateral com um item é moldura vazia.
 * Com a segunda seção isso deixou de valer, e moldura errada é o tipo de
 * erro que fica mais caro a cada tela nova que nasce dentro dela.
 *
 * ── Por que os itens não construídos aparecem ──────────────────────────
 *
 * A tentação é mostrar só o que existe. O protótipo faz o contrário — traz
 * o mapa inteiro com o Cronograma marcado `FASE 2` — e está certo: o
 * técnico precisa saber que a bancada de granulometria vai existir e que o
 * lugar dela é ali. O que não pode é o item parecer clicável e não abrir.
 *
 * Então: quem existe é link; quem não existe é texto apagado com a etiqueta
 * do que falta, `aria-disabled`, fora da ordem de tabulação. O mapa aparece
 * inteiro sem prometer nada que não entrega.
 */

/**
 * Telas que pedem a tela inteira, e não a coluna de leitura.
 *
 * O `.conteudo` para em 1100 px porque quase tudo aqui é texto e tabela
 * estreita, e linha de texto larga demais cansa de ler. A matriz de
 * permissões é a exceção: uma coluna por módulo, e o número de módulos só
 * cresce. Espremida em 1100 px ela vira barra de rolagem lateral — e barra
 * lateral numa matriz é o pior caso, porque compara-se **linha com linha**
 * e a pessoa perde a coluna de referência ao arrastar.
 *
 * A lista é explícita, e não uma regra automática, para que largar a
 * coluna de leitura continue sendo uma decisão de quem escreve a tela.
 */
const TELA_LARGA = new Set(['/usuarios', '/', '/painel', '/comercial/painel']);
/* (552, T28-05) O painel do Comercial é o mesmo molde do painel do laboratório: a mesma largura. */
/* (240) O painel do laboratório: cinco cartões lado a lado e dois
   gráficos por linha pedem a largura da matriz, não a coluna de leitura. */
/*
 * E as que pedem a largura INTEIRA, sem teto (onda 200): o cronograma. Uma
 * linha do tempo é a única tela em que cada pixel a mais é um dia a mais
 * na janela — «para monitores maiores poderia aproveitar melhor o espaço
 * lateral» (Humberto, 22/09). O teto de 1600 px da matriz não vale aqui,
 * porque a leitura é por barra, não por linha de texto.
 */
/* E o mapa dos ensaios (onda 215): «aproveite a largura total da página»
   — mapa é como linha do tempo, cada pixel a mais é chão a mais. */
const TELA_INTEIRA = new Set(['/cronograma', '/mapa']);
/*
 * E as LISTAS (onda 223). Combinado na aba do Layout SGQ quando se desenhou
 * a tabela com ficha (212): «a tabela ocupa a largura toda da área de
 * conteúdo» — e cobrado pelo Humberto em 29/09, porque a 212 e a 219
 * trocaram a tabela e esqueceram a coluna. Numa lista, a largura é
 * coluna a mais sem rolagem, e é o espaço que a ficha de 400 px ocupa ao
 * abrir sem espremer a tabela. A coluna de 1100 px continua valendo para
 * o que se LÊ (a OS aberta, a ficha do ensaio, os formulários).
 */
const TELA_DE_LISTA = new Set([
  // Trabalho
  '/os', '/recebimentos', '/amostras', '/descarte', '/campo', '/cptu', '/spt',
  // Bancada e saída
  '/fila', '/assinatura', '/medicoes', '/itens-medicao', '/portal',
  // SGQ
  '/equipamentos', '/padroes', '/equipamentos/pendencias',
  '/equipamentos/verificacoes', '/normas', '/calculos',
  '/nao-conformidades', '/competencias', '/treinamentos',
  // Cadastros e sistema
  '/cadastros', '/clientes', '/fornecedores', '/locais',
  '/categorias-de-equipamento', '/relatos',
]);

/*
 * E a OS aberta, em todas as abas (256): «metade da área útil fica vazia»
 * (U6 da ideia do fluxo da OS). A tabela de amostras e o escopo são
 * tabelas largas; a régua de seis etapas pede a linha inteira. Desde a 257
 * a programação é uma aba como as outras; o campo segue como está. O
 * cronograma (304) também: o Gantt é a tela mais larga da OS («a aba
 * cronograma não está com a largura total» — Humberto, 15/10/2026).
 */
const TELA_DA_OS = /^\/os\/(?!nova$)[^/]+(?:\/(?:escopo|amostras|programacao|cronograma|quantitativos|historico))?$/;

/*
 * E a campanha CPTu aberta, em todas as abas, e a importação (312): a mesma
 * moldura de registro da OS — régua de seis etapas e tabela de nove colunas
 * —, e a conferência de dez verticais com os campos lado a lado. A vertical
 * aberta (313, R3: `/cptu/:id/verticais/:vid`, e as vistas dela) tem três
 * colunas — a lista, as trilhas e a leitura com o ábaco — e também é larga;
 * a dissipação aberta (`/cptu/:id/dissipacoes/:did`) e a aba Premissas, idem;
 * a aba Relatório (315: as seções, o editor e «O que entra no PDF»), também.
 */
/*
 * E a caixa de entrada do Comercial (onda 550): a lista, a conversa e o
 * painel do cliente lado a lado (spec do Comercial §8.1) pedem a largura
 * inteira — é a tela em que a pessoa passa o dia. O quadro do funil (onda
 * 550), também: as sete colunas lado a lado (spec §8.3).
 */
const TELA_DO_COMERCIAL = /^\/comercial\/(?:caixa|funil|conversa\/[^/]+)$/;

/* 361: a campanha SPT (furos e relatório) usa a página inteira — a ficha do furo tem tabela, perfil e classificação lado a lado.
   372: a edição rápida também (pedido do Humberto, 03/11/2026: «deixe largura total para não ficar o scroll horizontal»). */
const TELA_DA_CAMPANHA_SPT = /^\/spt\/[^/]+(?:\/(?:relatorio|edicao|furos\/[^/]+))?$/;
const TELA_DA_CAMPANHA = /^\/cptu\/[^/]+(?:\/(?:dissipacoes(?:\/[^/]+)?|premissas|analise(?:\/(?:perfis|3d))?|relatorio|historico|importar|verticais\/[^/]+(?:\/(?:tabela|edicao|camadas))?))?$/;

/**
 * A barra inferior do celular — cinco alvos, e nada mais (onda 163; 308).
 *
 * A gaveta lateral mostra o mapa INTEIRO do sistema, e é isso que a faz
 * boa no tablet e no desktop e cara no celular: para chegar à fila, o
 * técnico abre a gaveta, procura o grupo «Bancada» e toca no item. Três
 * gestos, com o polegar subindo até o canto superior esquerdo, que é o
 * ponto mais distante da mão que segura o aparelho.
 *
 * A barra põe o que ele usa o dia inteiro debaixo do polegar, e deixa a
 * gaveta para o resto — que continua alcançável em «Mais».
 *
 * Os atalhos não são fixos: são os primeiros desta lista que o PERFIL
 * alcança. O auxiliar que não vê o painel não recebe um atalho para uma
 * recusa, pela mesma razão que o menu já não lhe mostra o item (UX-015).
 *
 * ── Quatro atalhos e o «Mais» (onda 308, spec 06 D1) ───────────────────
 *
 * «Hoje no Labmanager temos os seguintes botões fixos abaixo: Fila,
 * Amostras, Receber, Mais. Acredito que devemos deixar OS, Fila, Receber,
 * mais» (Humberto, 08/10/2026) — e, vendo o canvas, ele pôs a Estufa no
 * meio, com o número de cápsulas esperando a pesagem seca. Eram três
 * atalhos (`ALVOS_DA_BARRA`); são quatro: a 412 px do Galaxy A17 cada alvo
 * fica com 82 px de largura, folgado para os 44 × 44 da RN-251 (a). Amostras
 * e Painel ficam na reserva: quem não alcança a Fila ou a Estufa (a recepção,
 * por exemplo) recebe os da reserva no lugar, até onde o perfil alcança.
 *
 * ── O Painel de quem gerencia (onda 308, spec 06b M5) ─────────────────
 *
 * A melhoria M5, aprovada com a spec 06b: no LabManager, quem alcança o
 * Painel (`indicador.ver`, quem gerencia) tem o Painel no lugar da Estufa —
 * OS · Fila · Painel · Receber · Mais —, e a Estufa vai para a reserva, no
 * lugar que era do Painel: fora da barra, ela mora no «Mais», e o número
 * dela não é perguntado (a Moldura só conta a Estufa que está na barra).
 * Quem não alcança o Painel continua com a D1.
 *
 * ── Uma lista por espaço (onda 293, spec 03 §3.2) ──────────────────────
 *
 * A barra é a do espaço ABERTO. Com os três espaços (Hub, LabManager,
 * Campo), a lista única de antes — fila, amostras, recebimento — seria a
 * bancada do laboratório dentro do Campo, e tocar nela levaria a pessoa a
 * um módulo que o menu do espaço nem mostra. A lista do `labmanager` é a da
 * D1 (acima: OS, Fila, Estufa, Receber, com Amostras e Painel de reserva); a
 * do `hub` tem o Início, a OS, o mapa e a medição; a do `campo` tem só dois módulos que
 * lhe pertencem, e a barra fica com dois atalhos mais o «Mais». Só entram
 * módulos que moram no espaço (a OS e os equipamentos moram em mais de um, e
 * tocar neles não troca de espaço — RN-240), e a troca de espaço no celular
 * mora no topo da folha «Mais» (no tablet, no topo da gaveta).
 */
const ATALHOS: Record<EspacoDeTrabalho, { chave: string; curto: string; exige?: string; longo?: string }[]> = {
  labmanager: [
    { chave: 'os', curto: 'OS' },
    { chave: 'fila', curto: 'Fila' },
    { chave: 'estufa', curto: 'Estufa' },
    { chave: 'recebimento', curto: 'Receber' },
    { chave: 'amostras', curto: 'Amostras' },
    { chave: 'painel', curto: 'Painel' },
  ],
  /* 550 (Tarefa 20): o Início, a porta do Hub, vem primeiro — no celular,
     voltar a «O que precisa de você hoje» é um toque, e não o «Mais». */
  hub: [
    { chave: 'inicio', curto: 'Início' },
    { chave: 'os', curto: 'OS' },
    { chave: 'mapa', curto: 'Mapa' },
    { chave: 'medicao', curto: 'Medição' },
  ],
  campo: [
    { chave: 'campo', curto: 'Campo' },
    { chave: 'equipamentos', curto: 'Equip.' },
  ],
  /* D6 (Humberto, 01/11/2026): Caixa · Funil · Oportunidades para todos, e o
     Painel como o quarto atalho só da diretoria (`comercial.diretoria`); quem
     não é da diretoria acha o painel no «Mais». */
  comercial: [
    { chave: 'caixa', curto: 'Caixa' },
    { chave: 'funil', curto: 'Funil' },
    /* «Oportunidades» não cabe nos 82 px do alvo a 412 px: a abreviação da casa (como «Equip.»), e o nome
       inteiro no rótulo do leitor de tela. */
    { chave: 'oportunidades', curto: 'Oportun.', longo: 'Oportunidades' },
    { chave: 'painel_comercial', curto: 'Painel', exige: 'comercial.diretoria' },
  ],
};

/** Quantos atalhos a barra mostra antes do «Mais» (D1, spec 06). */
const ALVOS_DA_BARRA = 4;

/**
 * Se o módulo pode ser atalho da barra para quem tem `permissoes`: no ar, com
 * rota, e alcançado (a regra de `modulos.ts`). O mesmo critério para pôr o
 * atalho na barra e para decidir a ordem (M5).
 */
function vaiNaBarra(m: Modulo | undefined, permissoes: string[]): m is Modulo {
  return !!m && !!m.rota && m.situacao === 'no_ar' && alcanca(m, permissoes);
}

/**
 * A lista de atalhos do espaço, na ordem em que a barra os procura. No
 * LabManager, quem alcança o Painel tem o Painel e a Estufa trocados de lugar
 * (M5, spec 06b §5.3): `[os, fila, painel, recebimento, amostras, estufa]`.
 */
function ordemDosAtalhos(
  permissoes: string[],
  espaco: EspacoDeTrabalho,
): { chave: string; curto: string; longo?: string }[] {
  /* D6: o atalho que pede uma permissão a mais só entra para quem a tem. */
  const lista = ATALHOS[espaco].filter((a) => !a.exige || permissoes.includes(a.exige));
  if (espaco !== 'labmanager' || !vaiNaBarra(MODULOS.find((x) => x.chave === 'painel'), permissoes)) {
    return lista;
  }
  const atalho = (chave: string) => lista.find((b) => b.chave === chave);
  return lista.map((a) => {
    if (a.chave === 'estufa') return atalho('painel') ?? a;
    if (a.chave === 'painel') return atalho('estufa') ?? a;
    return a;
  });
}

function atalhosDaBarra(
  permissoes: string[],
  espaco: EspacoDeTrabalho,
): { modulo: Modulo; curto: string; longo?: string }[] {
  /* A regra de alcance mora em `modulos.ts` desde a onda 175. Esta função
     tinha uma cópia dela, escrita antes; duas cópias da mesma regra são
     duas oportunidades de elas discordarem. */
  const achados: { modulo: Modulo; curto: string; longo?: string }[] = [];
  for (const a of ordemDosAtalhos(permissoes, espaco)) {
    if (achados.length === ALVOS_DA_BARRA) break;
    const m = MODULOS.find((x) => x.chave === a.chave);
    if (vaiNaBarra(m, permissoes)) {
      achados.push({ modulo: m, curto: a.curto, longo: a.longo });
    }
  }
  return achados;
}

/*
 * `estaAqui` (se a tela corrente é do módulo), `enderecoDoModulo` (para onde
 * o item leva) e `iniciais` (o disco da pessoa) moram em `regrasDaMoldura.ts`
 * desde a onda 308: o menu, a barra e a folha «Mais» fazem as mesmas
 * perguntas, e a resposta é uma só.
 */

/*
 * O espaço que a navegação trouxe (`location.state.espaco`), se for um espaço
 * de trabalho. Quem o põe é a porta (`Porta`, em `main.tsx`), o botão da
 * página de recusa (`RecusaDoEspaco`) e, desde a onda 340, os próprios links
 * dos espaços (`linksDosEspacos`). Qualquer outra coisa no estado — de
 * outra tela, de outra versão — vale como nada.
 */
function espacoDaNavegacao(estado: unknown): EspacoDeTrabalho | null {
  const e = (estado as { espaco?: unknown } | null)?.espaco;
  return ESPACOS.find((x) => x.chave === e)?.chave ?? null;
}

/*
 * Os `<input>` que não abrem o teclado do celular: marcar, escolher, apertar,
 * arrastar, anexar. Todo o resto (texto, número, data, busca…) abre.
 */
const NAO_ABRE_TECLADO = new Set(['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'file', 'color', 'image', 'hidden']);

/**
 * Se o foco neste elemento abre o teclado do celular (onda 308, spec 06 §5.7):
 * um campo de digitar, uma área de texto ou um trecho editável. Campo
 * travado (`readOnly`, `disabled`) recebe foco sem abrir teclado.
 */
function abreTeclado(el: Element | null): boolean {
  if (el instanceof HTMLInputElement) return !NAO_ABRE_TECLADO.has(el.type) && !el.readOnly && !el.disabled;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
  return el instanceof HTMLElement && el.isContentEditable;
}

/** Quanto a tela encolhe, no mínimo, quando o teclado abre (Ruling R34). */
const ENCOLHE_COM_O_TECLADO = 150;

/*
 * A maior altura de tela já vista em cada largura (R34). Por largura: girar
 * o aparelho troca a largura, e a referência troca junto. No módulo, e não
 * no estado da `Moldura`: uma `Moldura` montada de novo com o teclado aberto
 * tomaria a tela encolhida por tela inteira, e nunca mais veria o teclado.
 */
const MAIOR_ALTURA = new Map<number, number>();

/*
 * A altura da tela agora. Com `interactive-widget=resizes-content` (Android)
 * o teclado encolhe a janela de layout (`innerHeight`); onde ele só encolhe
 * a janela visual (iOS), quem encolhe é o `visualViewport`. O menor dos dois
 * — a janela visual de volta à escala da página, porque o zoom de pinça
 * também a encolhe, e zoom não é teclado.
 */
function alturaDaTela(): number {
  const visual = window.visualViewport;
  return visual ? Math.min(window.innerHeight, visual.height * visual.scale) : window.innerHeight;
}

/**
 * Se o teclado do celular está aberto (R34): o foco num campo de digitar E a
 * tela pelo menos 150 px menor que a maior já vista nesta largura. Só o foco
 * não basta — o autofoco de uma tela que abre, o teclado fechado pelo
 * «voltar» do Android e o seletor de data deixam o foco no campo com a tela
 * inteira. Guarda a altura de agora, se for a maior da largura.
 */
function tecladoAberto(): boolean {
  const largura = window.innerWidth;
  const altura = alturaDaTela();
  const maior = Math.max(MAIOR_ALTURA.get(largura) ?? 0, altura);
  MAIOR_ALTURA.set(largura, maior);
  return abreTeclado(document.activeElement) && maior - altura >= ENCOLHE_COM_O_TECLADO;
}

/**
 * Quais grupos do menu ficam abertos.
 *
 * "Teremos que agrupar em categorias no menu lateral e abrir apenas
 * quando for abrir aquela sessão" (Testesolo, 23/08/2026).
 *
 * A regra tem três camadas, nesta ordem:
 *
 *  1. **O grupo da tela em que você está abre sempre.** Se ele ficasse
 *     fechado, o item marcado como corrente estaria escondido — e um
 *     indicador de "você está aqui" que não se vê não indica nada.
 *  2. **O que você abriu ou fechou fica como você deixou**, entre
 *     sessões. Reabrir o mesmo grupo toda manhã é o tipo de atrito que
 *     faz a pessoa desistir do recurso.
 *  3. **Na primeira visita, tudo aberto.** O menu apresentado fechado
 *     esconde o mapa justamente de quem ainda não o conhece.
 *
 * A memória mora no `localStorage` e é enfeite: se o navegador recusar
 * (janela anônima, cota estourada), o menu cai na camada 3 e continua
 * inteiro. Por isso o `try`.
 *
 * ── Por espaço (onda 293) ────────────────────────────────────────────
 *
 * O Hub e o LabManager têm, cada um, um grupo «Trabalho» — e fechar um não
 * pode fechar o outro. A memória guarda `«espaço:grupo»` (`hub:Trabalho`),
 * e é a `chaveDoGrupo` que monta a chave. Os valores de antes da onda (só
 * o nome do grupo, sem os dois-pontos) não dizem de QUAL espaço eram:
 * `lerFechados` os descarta, e na primeira visita depois da onda todos os
 * grupos aparecem abertos, como na camada 3.
 */
const CHAVE_GRUPOS = 'labmanager.menu.fechados';
const chaveDoGrupo = (espaco: EspacoDeTrabalho, grupo: string) => `${espaco}:${grupo}`;

/*
 * 266 · o menu recolhe para o TRILHO (o «rail» do Carbon) — em todas as
 * telas. «Nesta tela o menu lateral pode recolher para o trilho? Sim,
 * pode aplicar isso para todas as telas também» (Humberto, 03/10/2026).
 *
 * Recolhido, o menu é uma faixa de 48 px só com os ícones; ao passar o
 * ponteiro ou ao chegar pelo Tab ele se abre POR CIMA do conteúdo, sem
 * empurrar a tela. Quem prefere o menu sempre aberto fixa pelo botão do
 * topo do menu, e a escolha fica no aparelho. O padrão é recolhido: a
 * largura que o menu devolve é a da tela de trabalho.
 */
const CHAVE_TRILHO = 'labmanager.menu.trilho';
function lerTrilho(): boolean {
  try { return localStorage.getItem(CHAVE_TRILHO) !== 'aberto'; } catch { return true; }
}
function gravarTrilho(recolhido: boolean) {
  try { localStorage.setItem(CHAVE_TRILHO, recolhido ? 'recolhido' : 'aberto'); } catch { /* conforto */ }
}

function lerFechados(): Set<string> {
  try {
    const cru = localStorage.getItem(CHAVE_GRUPOS);
    if (!cru) return new Set();
    const lista = JSON.parse(cru);
    /* Só as chaves `espaço:grupo`: o valor antigo (o nome do grupo) some. */
    return Array.isArray(lista)
      ? new Set(lista.filter((x): x is string => typeof x === 'string' && x.includes(':')))
      : new Set();
  } catch {
    return new Set();
  }
}

function gravarFechados(s: Set<string>) {
  try {
    localStorage.setItem(CHAVE_GRUPOS, JSON.stringify([...s]));
  } catch {
    /* memória de conforto; a ausência dela não quebra o menu */
  }
}

/*
 * O espaço que a pessoa escolheu na faixa (onda 293, spec 03 §3.2).
 *
 * Fica no `sessionStorage`, e não no `localStorage` como as outras memórias
 * de conforto, de propósito: o espaço é de ONDE a pessoa está trabalhando
 * AGORA, e não uma preferência de aparelho. Uma aba nova começa sem escolha
 * — o espaço sai do endereço (`espacoDaTela`) —, e a mesma aba lembra dela
 * ao recarregar e ao trocar de tela, que é quando a `Moldura` pode ser
 * montada de novo. Duas abas abertas em espaços diferentes não brigam.
 *
 * Como as outras memórias, é enfeite: se o navegador recusar, a faixa cai
 * na regra do endereço e continua inteira. Por isso o `try`.
 */
const CHAVE_ESPACO = 'labmanager.espaco';
function lerEspaco(): EspacoDeTrabalho | null {
  try {
    const v = sessionStorage.getItem(CHAVE_ESPACO);
    return ESPACOS.find((e) => e.chave === v)?.chave ?? null;
  } catch {
    return null;
  }
}

function gravarEspaco(e: EspacoDeTrabalho) {
  try {
    sessionStorage.setItem(CHAVE_ESPACO, e);
  } catch {
    /* memória de conforto; a ausência dela não quebra a faixa */
  }
}

/**
 * A marca do topo — a do espaço (P-MRC-06, ondas 301 e 346).
 *
 * Escrita UMA vez, porque mora em dois lugares: no `Link` da marca (quem
 * alcança um espaço só, e qualquer pessoa abaixo de 900 px) e dentro do botão
 * do seletor de espaço (quem alcança dois ou mais, no PC). O 200.30 e o 288.2
 * leem ESTE bloco.
 *
 * A marca é o caminho de volta (23/08) — e, desde a onda 211, mora no
 * cabeçalho, como no Carbon: a faixa de 48 px que atravessa a largura toda é
 * o lugar onde a Testesolo assina. O arquivo oficial entra sobre a chapa
 * creme para a qual foi desenhado; recolorir seria adulterar a marca (171).
 *
 * Onda 301 — «a logo erlenmeyer sem texto deve aparecer no local da logo da
 * Testesolo no LabManager» (Humberto, 07/10): quando a marca fala do
 * LabManager, o ícone dele (o arquivo da 300, sem texto), igual nos dois
 * temas — o desenho tem fundo próprio. No Hub, no Campo e no Comercial
 * (onda 550), a marca da Testesolo na `.chapa`: o erlenmeyer é só do
 * LabManager (P-MRC-06, P-MRC-08). É o `espacoDaMarca` (295) que decide: na
 * recusa, a marca é a do espaço de volta.
 */
function MarcaDoEspaco({ espacoDaMarca }: { espacoDaMarca: EspacoDeTrabalho | null }) {
  return espacoDaMarca === 'labmanager' ? (
    <img className="marca-labmanager" src="/marca-labmanager.svg" alt="LabManager"
      onError={(e) => { e.currentTarget.style.display = 'none'; }} />
  ) : (
    <span className="chapa">
      <img className="marca-cor" src="/marca.png" alt="Testesolo"
        onError={(e) => { e.currentTarget.style.display = 'none'; }} />
      {/* No escuro, a branca (tirada da preta oficial) e sem chapa;
          qual das duas aparece é o tema que decide, pelos tokens
          `--ts-marca-*`. */}
      <img className="marca-branca" src="/marca-branca.png" alt="Testesolo"
        onError={(e) => { e.currentTarget.style.display = 'none'; }} />
    </span>
  );
}

export function Moldura({ children }: { children: React.ReactNode }) {
  const { eu, sair, pode } = useSessao();
  const [trocaDePersona, setTrocaDePersona] = useState(false);
  const local = useLocation();
  const onde = local.pathname;
  const [menuAberto, setMenuAberto] = useState(false);
  /* A folha «Mais» do celular (onda 308, D2): o «Mais» da barra a abre no
     lugar da gaveta abaixo de 640 px. */
  const [folhaAberta, setFolhaAberta] = useState(false);
  const [trilho, setTrilho] = useState<boolean>(lerTrilho);
  const [tema, setTema] = useState<Tema>(lerTema);
  const [conexao, setConexao] = useState<EstadoDaConexao>(estadoDaConexao);
  const [naFila, setNaFila] = useState(tamanhoDaFila);
  const [verFilaAberta, setVerFilaAberta] = useState(false);
  const [fechados, setFechados] = useState<Set<string>>(lerFechados);
  const [escolhido, setEscolhido] = useState<EspacoDeTrabalho | null>(lerEspaco);
  const [engrenagem, setEngrenagem] = useState(false);
  const caixaEngrenagem = useRef<HTMLDivElement>(null);
  /* O seletor de espaço (onda 346): a janelinha aberta pelo botão do topo. */
  const [seletorAberto, setSeletorAberto] = useState(false);
  const caixaSeletor = useRef<HTMLDivElement>(null);
  const botaoSeletor = useRef<HTMLButtonElement>(null);
  /* Abaixo de 900 px a faixa esconde os espaços (a gaveta e a folha «Mais»
     os levam) e o seletor não existe: a mesma régua do CSS. */
  const abaixoDe900 = useEstreito(900);
  /* O rabo da trilha que a tela corrente declarou (`useCaminho`, onda
     188). `useCallback` porque a função é dependência do efeito de quem
     declara: uma função nova a cada render seria uma redeclaração a
     cada render. */
  const [caminhoDeclarado, setCaminhoDeclarado] = useState<Elo[] | null>(null);
  const declararCaminho = useCallback((elos: Elo[] | null) => setCaminhoDeclarado(elos), []);
  const contextoDoCaminho = useMemo(() => ({ declarar: declararCaminho }), [declararCaminho]);

  useEffect(() => { aplicarTema(tema); }, [tema]);
  useEffect(() => { setMenuAberto(false); setEngrenagem(false); setFolhaAberta(false); setSeletorAberto(false); }, [onde]);

  /*
   * A engrenagem fecha ao clicar fora e ao apertar Esc.
   *
   * Sem isto ela ficaria aberta por cima da tela até alguém clicar nela
   * de novo — e um painel que só fecha onde abriu é o defeito clássico
   * de menu suspenso: a pessoa clica no conteúdo atrás, nada acontece, e
   * ela conclui que a tela travou.
   */
  useEffect(() => {
    if (!engrenagem) return undefined;
    const clique = (e: MouseEvent) => {
      if (!caixaEngrenagem.current?.contains(e.target as Node)) setEngrenagem(false);
    };
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape') setEngrenagem(false); };
    document.addEventListener('mousedown', clique);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('mousedown', clique);
      document.removeEventListener('keydown', tecla);
    };
  }, [engrenagem]);

  /*
   * O seletor de espaço fecha ao clicar fora e ao apertar Esc — e o Esc
   * devolve o foco ao botão que o abriu, para quem navega pelo teclado não
   * ficar perdido no fim da página. Escolher um espaço fecha também (em
   * `escolherEspaco`), e o Tab que sai da janelinha idem (`aoSairDoSeletor`).
   */
  useEffect(() => {
    if (!seletorAberto) return undefined;
    const clique = (e: MouseEvent) => {
      if (!caixaSeletor.current?.contains(e.target as Node)) setSeletorAberto(false);
    };
    const tecla = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setSeletorAberto(false);
      botaoSeletor.current?.focus();
    };
    document.addEventListener('mousedown', clique);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('mousedown', clique);
      document.removeEventListener('keydown', tecla);
    };
  }, [seletorAberto]);

  /*
   * O espaço aberto (onda 293, spec 03 §3.2): «Hub · LabManager · Campo».
   *
   * «Precisamos definir como será o layout/estrutura do Hub entre
   * LabManager e Módulo de Campo […]. Quem trabalha no laboratório não terá
   * acesso ao módulo de campo e quem trabalha em campo […] não terá acesso
   * ao LabManager» (Humberto, 06/10/2026). Na H0 a faixa e o menu já eram os
   * dos espaços; na H1 (onda 295) o recorte por perfil vale: quem alcança o
   * quê vem da SESSÃO (`eu.espacos`), e não mais de um palpite pelo prefixo
   * das permissões.
   *
   * O espaço segue o ENDEREÇO (`espacoDaTela`, RN-240): `/mapa` é do Hub,
   * `/fila` é do LabManager. Só o módulo que mora em mais de um espaço — a
   * OS, o parque de equipamentos — é ambíguo, e aí vale o que a pessoa
   * ESCOLHEU na faixa; o mesmo vale para as telas da engrenagem e para o
   * endereço que nenhum módulo reclama, que não são espaço de ninguém e
   * mantêm o que estava. Seguir um link dentro de uma tela, portanto, não
   * muda o espaço, e clicar num módulo compartilhado do menu também não.
   *
   * `escolhido` é a memória disso: quando o espaço da tela muda (a pessoa
   * foi a um endereço que é só de um espaço), ele passa a ser o escolhido,
   * e é gravado. O CLIQUE na faixa grava a escolha ANTES de navegar — é o
   * que deixa «Hub» valer estando em `/os` no LabManager: o endereço é o
   * mesmo, só a escolha muda. O clique modificado (Ctrl, Cmd, Shift, Alt)
   * abre outra aba e não grava nada nesta (`cliqueNoEspaco`).
   *
   * Só o que a pessoa alcança entra na faixa (`espacosDaSessao`, a partir de
   * `eu.espacos`); e o escolhido de uma sessão anterior que ela já não
   * alcança — depois de «Entrar como» outra pessoa — não vale.
   *
   * O espaço da tela pode ser um que a pessoa NÃO alcança (o técnico que abre
   * `/mapa` pede o Hub). Esse espaço não é gravado como escolhido — a
   * escolha é de onde ela trabalha, e não de onde a tela pediu —, e o menu
   * lateral e os atalhos do celular ficam vazios: não se oferece o que a API
   * recusaria. A página que explica a recusa é da `RecusaDoEspaco`.
   */
  const permissoes = eu?.permissoes ?? [];
  const alcancados = espacosDaSessao(eu?.espacos ?? []);
  /*
   * O espaço pode vir na NAVEGAÇÃO (H1, onda 295): a porta leva quem tem
   * vários espaços ao Hub, na lista de OS — que também é do LabManager. Sem o
   * espaço na navegação, a aba que lembrava o LabManager abriria a lista
   * nele (ponto de atenção 1). Vale como o escolhido NO MESMO render do
   * endereço novo, e por isso é lido aqui e não num efeito: é o mesmo cuidado
   * do aviso «ATENÇÃO» abaixo, de o espaço e o endereço aterrissarem juntos.
   * O efeito logo abaixo o grava como escolhido, como qualquer espaço da tela.
   * Só vale se a pessoa o alcança — depois de «Entrar como», um estado velho
   * não abre um espaço que a persona não tem.
   */
  const daNavegacao = espacoDaNavegacao(local.state);
  const escolhidoAgora = daNavegacao && alcancados.includes(daNavegacao) ? daNavegacao : escolhido;
  const espaco = espacoDaTela(onde, escolhidoAgora, alcancados);
  const alcancaOEspaco = alcancados.includes(espaco);
  /*
   * A recusa (spec 03 §10): a tela é de um espaço que a pessoa não alcança, ou
   * a conta não alcança espaço nenhum. A página que explica entra no lugar da
   * tela (`RecusaDoEspaco`, `ContaSemAcesso`); `/` e a engrenagem nunca são
   * recusadas (`recusaDaTela`).
   *
   * A marca e o nome ao lado dela falam sempre de um espaço que a pessoa TEM:
   * na recusa, o de volta (o técnico em `/mapa` está no LabManager, e não num
   * Hub que ele não tem); em `/`, que o registro dá ao Painel, o primeiro dela
   * — sem isso o sondador veria «LabManager» ao lado da marca no instante em
   * que a porta o leva ao Campo. A conta sem espaço não tem nenhum: a marca
   * leva a `/`, que é onde a página dela mora.
   */
  const recusa = recusaDaTela(onde, escolhidoAgora, alcancados);
  const espacoDaMarca: EspacoDeTrabalho | null = recusa?.tipo === 'espaco' ? recusa.voltar
    : alcancaOEspaco ? espaco : alcancados[0] ?? null;
  useEffect(() => {
    if (alcancaOEspaco && espaco !== escolhido) { setEscolhido(espaco); gravarEspaco(espaco); }
  }, [alcancaOEspaco, espaco, escolhido]);
  /*
   * ATENÇÃO — esta função e a navegação do `Link` precisam aterrissar no MESMO
   * render. O clique chama `setEscolhido` e, logo depois, o `Link` navega; no
   * React 18 sem `startTransition` os dois entram no mesmo lote e a tela
   * renderiza já com o espaço novo e o endereço novo. Com
   * `future.v7_startTransition` (e no React Router 7, onde ele é o padrão) a
   * navegação vira transição: o `setEscolhido`, urgente, renderiza ANTES do
   * endereço novo, o efeito acima vê o espaço do endereço ANTIGO e grava a
   * escolha velha de volta por cima. O 280.41 e o 280.43 pegam isso — se algum
   * deles ficar vermelho depois de uma atualização do roteador, é aqui.
   *
   * Desde a onda 340 (React Router 7, React 19) é exatamente o caso, e as
   * duas guardas são estas: (1) a escolha também é transição
   * (`startTransition`), e as transições do mesmo clique entram no mesmo
   * render que o endereço novo; (2) o link leva o espaço no `state` da
   * navegação (`espacoDaNavegacao`), então o render do endereço novo já sabe
   * o espaço, mesmo que algum render chegue antes dele.
   *
   * Fecha a gaveta (e a folha «Mais» do celular) porque quem escolhe um espaço
   * escolheu sair dela: a porta do espaço pode ser a página em que a pessoa já
   * está (a do Hub é `/os`), e aí o endereço não muda e o efeito de `onde` não
   * fecharia nada.
   */
  const escolherEspaco = (e: EspacoDeTrabalho) => {
    startTransition(() => setEscolhido(e));
    gravarEspaco(e);
    setMenuAberto(false);
    setFolhaAberta(false);
    /* O seletor (onda 346) fecha, e o foco volta ao botão: o link em que a
       pessoa estava vai ficar oculto, e o foco não pode cair no vazio. Se o
       espaço escolhido tem a mesma porta da tela aberta, o endereço não muda
       e o efeito de `onde` não fecharia nada. */
    setSeletorAberto(false);
    botaoSeletor.current?.focus({ preventScroll: true });
  };

  /*
   * O clique no nome de um espaço. Só o clique que NAVEGA nesta aba escolhe o
   * espaço: com Ctrl, Cmd, Shift ou Alt, ou com um botão que não o primário, o
   * navegador abre o destino em OUTRA aba (ou janela, ou baixa), e esta tem de
   * ficar como estava. O `Link` do react-router roda o `onClick` da gente
   * ANTES de decidir não navegar nesses casos; sem esta guarda, o Ctrl+clique
   * em «Campo» estando em `/os` trocaria o menu e a faixa desta aba sem
   * navegar (e o `espacoDaTela` a devolveria ao Hub, pela regra 4). É UMA
   * guarda, aqui, porque os dois lugares que desenham os links (a faixa e a
   * gaveta) partem da mesma lista.
   */
  const cliqueNoEspaco = (ev: React.MouseEvent, e: EspacoDeTrabalho) => {
    if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey || ev.button !== 0) return;
    escolherEspaco(e);
  };

  /*
   * Os links dos espaços, escritos UMA vez. A faixa (desktop) e o topo da
   * gaveta (tablet) desenham a MESMA lista — o mesmo destino, o mesmo
   * `aria-current` e o mesmo clique que grava a escolha antes de navegar —, e
   * a folha de estilo mostra uma ou outra conforme a largura. Desde a onda 308
   * o topo da folha «Mais» do celular é a terceira, com a mesma lista. Duas
   * cópias do mapeamento seriam duas oportunidades de a gaveta e a faixa
   * discordarem sobre qual é o espaço aberto.
   *
   * Só entram os espaços que têm para onde levar (`espacosDaFaixa`): o
   * espaço alcançado por uma permissão sem tela no menu dele (uma exceção
   * individual com só `relatorio.disponibilizar`, por exemplo) levaria a `/`.
   */
  const naFaixa = espacosDaFaixa(alcancados, permissoes);
  const linkDoEspaco = (e: EspacoDeTrabalho, descrito?: string) => (
    <Link key={e} to={rotaDoEspaco(e, permissoes) ?? '/'} state={{ espaco: e }}
      className={`espaco ${e === espaco ? 'aqui' : ''}`}
      aria-current={e === espaco ? 'true' : undefined}
      aria-describedby={descrito}
      onClick={(ev) => cliqueNoEspaco(ev, e)}>
      {nomeDoEspaco(e)}
    </Link>
  );
  const linksDosEspacos = naFaixa.map((e) => linkDoEspaco(e));

  /*
   * O seletor de espaço (onda 346, spec do ERP §4.5, `docs/erp/2026-10-17-comercial-erp-design.md`): no PC, quem alcança dois
   * ou mais espaços os troca por UM botão no lugar da marca — que abre a
   * janelinha com os espaços dela, cada um com o nome e uma linha do que há
   * lá. É o que deixa o ERP crescer a oito espaços sem apertar a faixa do
   * topo. Quem alcança um só não tem o que escolher (continua a marca com o
   * `.produto`), e abaixo de 900 px a gaveta e a folha «Mais» fazem o papel.
   *
   * Os links são OS MESMOS (`linkDoEspaco`): o destino, o `aria-current` e o
   * clique que grava a escolha. A descrição não entra no link — o texto dele
   * continua sendo só o nome, para quem clica pelo nome —, mas na linha ao
   * lado, ligada a ele por `aria-describedby`; o link se estica sobre a
   * linha toda (`.item-espaco a::after`), então o alvo é a linha inteira.
   */
  const comSeletor = naFaixa.length > 1 && !abaixoDe900;
  const itensDoSeletor = naFaixa.map((e) => (
    <div key={e} className={`item-espaco${e === espaco ? ' aqui' : ''}`}>
      {linkDoEspaco(e, `descricao-espaco-${e}`)}
      <small id={`descricao-espaco-${e}`}>{descricaoDoEspaco(e)}</small>
    </div>
  ));
  useEffect(() => { if (!comSeletor) setSeletorAberto(false); }, [comSeletor]);
  /* O Tab que sai da janelinha a fecha. O `relatedTarget` nulo (o clique num
     lugar sem foco, ou o Safari, que não foca botão nem link ao clicar) fica
     para o clique fora, acima: fechar aqui engoliria o clique da opção. */
  const aoSairDoSeletor = (ev: React.FocusEvent) => {
    const para = ev.relatedTarget as Node | null;
    if (para && !caixaSeletor.current?.contains(para)) setSeletorAberto(false);
  };

  const alternarGrupo = (g: string) => {
    setFechados((antes) => {
      const novo = new Set(antes);
      if (novo.has(g)) novo.delete(g); else novo.add(g);
      gravarFechados(novo);
      return novo;
    });
  };

  /*
   * O chip do cabeçalho, e por que ele deixou de olhar `navigator.onLine`.
   *
   * `navigator.onLine` responde «tem rede» quando há wi-fi SEM internet —
   * que é exatamente a situação do canteiro com um repetidor ligado num
   * modem morto. Quem sabe de verdade é a última chamada que foi ou não
   * foi, e é isso que o `api()` passou a anunciar (onda 126).
   *
   * O terceiro estado, «tentando», é novo e é o que importa: desde a
   * `0144` uma chamada que falha por rede é REENVIADA, e reenviar é
   * seguro porque a chave da intenção impede a duplicata. Sem o chip, o
   * técnico veria a tela parada por dois segundos e tocaria de novo.
   */
  useEffect(() => aoMudarConexao(setConexao), []);

  /*
   * O que está guardado no aparelho e ainda não subiu (onda 127).
   *
   * Fica ao lado do estado da conexão porque as duas informações só
   * significam alguma coisa juntas: «sem conexão» com a fila vazia é
   * espera; «sem conexão» com sete na fila é sete lançamentos que o
   * técnico precisa lembrar de não apagar o aplicativo antes de subir.
   */
  useEffect(() => aoMudarFila(setNaFila), []);

  /*
   * Os atalhos da barra de baixo e o número da Estufa (onda 308, D1).
   *
   * A contagem só é perguntada quando o selo pode aparecer: no celular (acima
   * de 640 px a barra nem existe), com a Estufa entre os atalhos mostrados
   * (quem gerencia tem o Painel no lugar dela, M5), e para quem tem
   * `ensaio.ver` — a Estufa entra na barra com qualquer
   * `ensaio.*` (`alcanca`), mas a rota da contagem pede o `ver`, e perguntar
   * sem ele seria um 403 a cada minuto.
   */
  const estreito = useEstreito(640);
  const atalhos = alcancaOEspaco ? atalhosDaBarra(permissoes, espaco) : [];
  const naEstufa = useContagemDaEstufa(
    estreito && pode('ensaio.ver') && atalhos.some((a) => a.modulo.chave === 'estufa'));

  /*
   * Os ladrilhos da folha «Mais» (onda 308, spec 06 §5.3): o menu do espaço
   * aberto, pela mesma regra de alcance do menu lateral, SEM os módulos que
   * a barra já mostra — tocar «Mais» para achar a Fila, que está debaixo do
   * polegar, seria um segundo caminho para o mesmo lugar. A barra é a fonte
   * (`atalhos`): o que ela mostra para ESTE perfil sai da folha, e o que ela
   * deixou de mostrar (Amostras, para o técnico) entra.
   *
   * Só o que abre (no ar, com rota): o menu lateral mostra o que ainda não
   * existe apagado, com a etiqueta do que falta, porque ele é o mapa do
   * sistema; o ladrilho é um alvo de polegar, e um alvo que não abre é o
   * defeito que o menu evita («o que não pode é o item parecer clicável e
   * não abrir»). Grupo que fica vazio sai; na recusa (a tela é de um espaço
   * que a pessoa não alcança), nenhum — a folha fica só com a conta.
   */
  const naBarra = new Set(atalhos.map((a) => a.modulo.chave));
  const gruposDaFolha = (alcancaOEspaco ? menuPorGrupo(espaco) : [])
    .map((g) => ({
      ...g,
      itens: g.itens.filter((m) => !!m.rota && m.situacao === 'no_ar'
        && alcanca(m, permissoes) && !naBarra.has(m.chave)),
    }))
    .filter((g) => g.itens.length > 0);

  /*
   * «Entrar como…» na folha FECHA a folha antes de abrir a escolha da
   * persona: as duas são diálogos modais, e com a folha aberta por baixo o
   * Tab e o Esc dela disputariam com a persona (a guarda de teclas da folha
   * só reconhece o `<dialog>` nativo, e a persona é um `div.veu-modal`).
   */
  const entrarComoDaFolha = eu && pode('usuario.entrar_como') && !eu.atuandoPor
    ? () => { setFolhaAberta(false); setTrocaDePersona(true); }
    : null;

  /*
   * A faixa da rede (onda 308, spec 06 §5.5, RN-251 g).
   *
   * No celular o chip de conexão e o da fila saem do topo — não cabem ao lado
   * da busca —, e o aviso desce para uma faixa logo abaixo dele, de ponta a
   * ponta. Só quando é preciso: sem rede, reenviando, ou com lançamento
   * guardado no aparelho. Com rede e a fila vazia, nada: uma faixa que diz
   * «tudo certo» o dia inteiro ensina a não olhar para ela no dia em que ela
   * diz outra coisa. As frases são as do chip, ditas por inteiro: «sem
   * conexão» sozinho deixa o técnico em dúvida se para de digitar.
   *
   * O que ela diz sem rede depende de o espaço da tela ter fila (Ruling R52,
   * `faixaPrometeFila`): no Campo, a leitura de instrumento fica guardada no
   * aparelho e sobe depois — «o que você lançar sobe quando a rede voltar»; no
   * LabManager e no Hub, a gravação sem rede não fica guardada (a pesagem
   * tenta de novo e falha), e a faixa não promete o que não faz — «o que você
   * gravar agora não chega ao servidor». A conta dos guardados e o «Ver»
   * aparecem sempre que houver algo na fila, em qualquer espaço (o que o
   * técnico lançou no Campo continua guardado quando ele passa ao
   * LabManager). «Ver» abre a mesma fila de envio do chip do desktop.
   */
  const comFaixaDaRede = estreito && (conexao !== 'no_ar' || naFila > 0);
  const textoDaFaixa = [
    conexao === 'sem_rede'
      ? (faixaPrometeFila(espaco)
        ? 'Sem conexão — o que você lançar sobe quando a rede voltar'
        : 'Sem conexão — o que você gravar agora não chega ao servidor. Espere a rede voltar.')
      : conexao === 'tentando' ? 'Reenviando…' : null,
    naFila > 0 ? plural(naFila, 'lançamento guardado', 'lançamentos guardados') : null,
  ].filter(Boolean).join(' · ');

  /*
   * A barra some ao digitar (onda 308, spec 06 §5.7, ponto de revisão 2).
   *
   * Com o teclado aberto no Galaxy A17, o teclado e a barra de baixo juntos
   * deixam menos de metade da tela para a ficha, e a barra de ação («Gravar»,
   * «Próximo») ficava espremida entre os dois. Com o teclado aberto
   * (`tecladoAberto`: foco num campo de digitar E a tela encolhida, R34), a
   * `.app` leva `data-digitando`: a folha de estilo esconde a barra de baixo
   * e desce a barra de ação até o pé da tela — que, com
   * `interactive-widget=resizes-content` no `index.html`, é o topo do
   * teclado.
   *
   * A conta é refeita quando o foco entra, quando ele sai e quando a tela
   * muda de tamanho (a janela e a janela visual). O `focusout` não decide na
   * hora: ao passar de um campo para o outro o foco sai de um ANTES de entrar
   * no outro, e a barra piscaria. Ele confere na volta do laço
   * (`setTimeout(0)`), quando o `focusin` do próximo já aconteceu. Só no
   * celular: acima de 640 px a barra nem existe, nada é escutado e o atributo
   * não é escrito.
   */
  const [digitando, setDigitando] = useState(false);
  useEffect(() => {
    if (!estreito) { setDigitando(false); return undefined; }
    let conferencia: number | undefined;
    const conferir = () => setDigitando(tecladoAberto());
    const saiu = () => {
      window.clearTimeout(conferencia);
      conferencia = window.setTimeout(conferir, 0);
    };
    const visual = window.visualViewport;
    conferir();
    document.addEventListener('focusin', conferir);
    document.addEventListener('focusout', saiu);
    window.addEventListener('resize', conferir);
    visual?.addEventListener('resize', conferir);
    return () => {
      window.clearTimeout(conferencia);
      document.removeEventListener('focusin', conferir);
      document.removeEventListener('focusout', saiu);
      window.removeEventListener('resize', conferir);
      visual?.removeEventListener('resize', conferir);
    };
  }, [estreito]);

  /*
   * A altura do que gruda no topo (onda 308, Ruling R32): o topo, e a faixa
   * da rede logo abaixo dele quando ela aparece. Outras telas grudam coisas
   * logo abaixo desse bloco — as abas da OS e a faixa da ficha (310) — e o
   * «pular para» (`scroll-padding-top` do `html`, no `celular.css`) não pode
   * deixar o alvo atrás dele. A medida vai para `--altura-presa` no `<html>`,
   * e um `ResizeObserver` a refaz quando a faixa aparece, some ou quebra em
   * duas linhas. Só no celular: no PC nada é escrito, e quem usa a variável
   * cai no padrão de 48 px. O topo é achado dentro da `.app`, e não por um
   * `ref` no `<header>`: o `200.30` lê a tag do cabeçalho no fonte.
   */
  const appRef = useRef<HTMLDivElement>(null);
  const faixaRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const raiz = document.documentElement;
    if (!estreito) return undefined;
    const topo = appRef.current?.querySelector<HTMLElement>(':scope > .topo') ?? null;
    const medir = () => {
      const alturaDoTopo = topo?.getBoundingClientRect().height ?? 48;
      const faixa = faixaRef.current?.getBoundingClientRect().height ?? 0;
      raiz.style.setProperty('--altura-presa', `${Math.round(alturaDoTopo + faixa)}px`);
    };
    medir();
    const observador = typeof ResizeObserver === 'function' ? new ResizeObserver(medir) : null;
    if (topo) observador?.observe(topo);
    if (faixaRef.current) observador?.observe(faixaRef.current);
    return () => {
      observador?.disconnect();
      raiz.style.removeProperty('--altura-presa');
    };
  }, [estreito, comFaixaDaRede]);

  const nome = eu?.nome ?? eu?.perfil ?? '';

  return (
    <div className={`app${trilho ? ' com-trilho' : ''}`} ref={appRef}
      data-digitando={estreito && digitando ? '' : undefined}>
      {/*
        A faixa da persona vem ANTES do cabeçalho e atravessa a largura
        toda. Ela só existe na sessão emprestada — e aí precisa ser a
        primeira coisa que a pessoa vê ao olhar para a tela.
      */}
      <FaixaDaPersona />
        <header className="topo">
          <button
            type="button"
            className="so-estreito"
            aria-label="Abrir menu"
            aria-expanded={menuAberto}
            aria-controls="lateral"
            onClick={() => setMenuAberto((v) => !v)}
          >
            ☰
          </button>

          {/*
            * A marca é o caminho de volta (23/08) — e, desde a onda 211,
            * mora no cabeçalho, como no Carbon (o arquivo e a escolha por
            * espaço estão em `MarcaDoEspaco`).
            *
            * Desde a onda 293 ela volta ao INÍCIO DO ESPAÇO aberto, e não
            * mais ao Painel (que só mora no LabManager): quem está no Campo
            * e toca na marca fica no Campo.
            *
            * Desde a onda 346, quem alcança dois ou mais espaços e está no PC
            * não tem a marca como link: ela vai para dentro do botão do
            * seletor de espaço (abaixo), e o caminho de volta ao início é a
            * própria opção do espaço aberto, na janelinha.
            */}
          {comSeletor ? (
            <div className="troca-espaco-caixa" ref={caixaSeletor} onBlur={aoSairDoSeletor}>
              <button type="button" ref={botaoSeletor} className="troca-espaco"
                aria-expanded={seletorAberto} aria-controls="menu-espacos"
                aria-label={`${nomeDoEspaco(espacoDaMarca ?? espaco)}, trocar de espaço`}
                onClick={() => setSeletorAberto((v) => !v)}>
                <span className="marca-topo">
                  <MarcaDoEspaco espacoDaMarca={espacoDaMarca} />
                </span>
                <span className="troca-nome">{nomeDoEspaco(espacoDaMarca ?? espaco)}</span>
                <span className="seta" aria-hidden="true">▾</span>
              </button>
              {/*
                * Os espaços que a pessoa alcança (onda 293, spec 03 §3.2) —
                * o «header menu» do Carbon, agora numa janelinha: «Hub ·
                * LabManager · Campo» e, no Plano 2, os do ERP. Cada espaço
                * leva à sua porta (`rotaDoEspaco`); o aberto leva
                * `aria-current` e o fundo de campo. O Sistema fica na
                * engrenagem e nunca aparece aqui. Fechada, a janelinha fica
                * no DOM com `hidden`: quem só LÊ os links (o nome, o
                * `aria-current`) os acha; quem clica abre antes.
                *
                * O botão segue o padrão de divulgação (`aria-expanded` e
                * `aria-controls`), sem `aria-haspopup`: a janelinha é uma
                * navegação de links, e não um `role="menu"` com setas.
                */}
              <div className="menu-espacos" id="menu-espacos" hidden={!seletorAberto}>
                <nav className="espacos" aria-label="Espaços">
                  {itensDoSeletor}
                </nav>
              </div>
            </div>
          ) : (
            <Link to={(espacoDaMarca && rotaDoEspaco(espacoDaMarca, permissoes)) ?? '/'}
              className="marca-topo"
              aria-label={espacoDaMarca ? `Ir para o início do ${nomeDoEspaco(espacoDaMarca)}` : 'Ir para o início'}>
              <MarcaDoEspaco espacoDaMarca={espacoDaMarca} />
              {/* Com UM espaço só na faixa (a H1 dá a cada pessoa os seus), não
                  há o que escolher e o nome dele fica ao lado da marca, como
                  era «Labmanager». Com mais de um, no PC o seletor já diz o
                  nome, e abaixo de 900 px a gaveta e a folha «Mais» fazem a
                  troca (e a CSS esconde este nome). */}
              {naFaixa.length < 2 && espacoDaMarca && (
                <span className="produto">{nomeDoEspaco(espacoDaMarca)}</span>
              )}
            </Link>
          )}

          {/*
            * Abaixo de 900 px nada mudou desde a onda 293: a faixa dos
            * espaços continua no DOM e a CSS a esconde (a busca precisa do
            * lugar), e a troca de espaço passa para o topo da gaveta
            * (`espacos-gaveta`) e para a folha «Mais», com os mesmos links.
            */}
          {naFaixa.length > 1 && !comSeletor && (
            <nav className="espacos" aria-label="Espaços">
              {linksDosEspacos}
            </nav>
          )}

          {/*
            * No Comercial a busca do topo não oferece o que o espaço procura
            * (cliente, proposta P-…, conversa): ela se cala ali, e a caixa de
            * entrada tem a sua. A busca global do Comercial vem depois.
            */}
          {espaco === 'comercial' ? <span className="topo-vao" aria-hidden="true" /> : <BuscaGlobal />}

          <span
            className={`chip ${TOM_DA_CONEXAO[conexao]}`}
            role="status"
            title={RECADO_DA_CONEXAO[conexao]}
          >
            {ROTULO_DA_CONEXAO[conexao]}
          </span>

          {naFila > 0 && (
            <button
              type="button"
              className="chip atencao chip-fila"
              title={`${naFila} lançamento(s) guardado(s) neste aparelho, `
                + 'esperando rede. Toque para ver e tentar agora.'}
              onClick={() => setVerFilaAberta(true)}
            >
              ⇡ {naFila} na fila
            </button>
          )}

          {/* O sino dos avisos (onda 548): quantos faltam ler, e a lista em /avisos. */}
          {eu && <SinoDeAvisos usuarioId={eu.usuarioId} />}

          {eu && (
            /*
             * A engrenagem, ao lado do nome.
             *
             * "Usuários e acessos e Relatos do Beta deveriam ficar em um
             * botão de configuração à parte, próximo ao nome do usuário"
             * (Testesolo, 23/08/2026).
             *
             * O critério que ficou: no menu o que você FAZ — o trabalho
             * do dia, o que produz registro técnico; na engrenagem o que
             * você CONFIGURA. Tema e Sair vêm junto porque eram dois
             * botões soltos no cabeçalho pela mesma razão errada: não
             * tinham onde morar.
             */
            <div className="engrenagem" ref={caixaEngrenagem}>
              <button
                type="button"
                className="quem"
                aria-haspopup="menu"
                aria-expanded={engrenagem}
                onClick={() => setEngrenagem((v) => !v)}
              >
                <span className="disco" aria-hidden="true">{iniciais(nome)}</span>
                <span className="quem-txt">
                  <b>{nome}</b>
                  <em>{rotulo(eu.perfil)}</em>
                </span>
                <span className="eng" aria-hidden="true">⚙</span>
              </button>

              {engrenagem && (
                <div className="engrenagem-caixa" role="menu">
                  {itensDaEngrenagem().map((m) => (
                    <Link key={m.chave} to={m.rota as string} role="menuitem"
                      className="engrenagem-item">
                      <span className="ic" aria-hidden="true"><Icone nome={iconeDoModulo(m.chave)} /></span>{m.titulo}
                    </Link>
                  ))}
                  <button
                    type="button"
                    role="menuitem"
                    className="engrenagem-item"
                    onClick={() => setTema((t) => proximoTema(t))}
                  >
                    <span className="ic" aria-hidden="true">◐</span>
                    Aparência
                    {/* O nome do tema ATUAL, e não o do próximo: o item
                        diz onde você está antes de dizer para onde vai. */}
                    <em className="valor">{NOME_TEMA[tema]}</em>
                  </button>
                  {/*
                    * «Entrar como» mora na engrenagem, e não no menu.
                    *
                    * O critério da casa: no menu o que você FAZ, na
                    * engrenagem o que você CONFIGURA. Vestir outra
                    * persona é configurar a própria sessão — e é o item
                    * que menos gente vê, porque só o admin tem a
                    * permissão (0079).
                    */}
                  {/*
                    * 0211 (RN-214): a exclusão definitiva. Fora do
                    * registro de módulos de propósito — não é módulo de
                    * ninguém, é uma porta de uma pessoa, na homologação.
                    */}
                  {eu.exclusaoDefinitiva && (
                    <Link to="/exclusao-definitiva" role="menuitem"
                      className="engrenagem-item perigo"
                      onClick={() => setEngrenagem(false)}>
                      <span className="ic" aria-hidden="true">⌫</span>
                      Exclusão definitiva
                    </Link>
                  )}
                  {pode('usuario.entrar_como') && !eu.atuandoPor && (
                    <button type="button" role="menuitem"
                      className="engrenagem-item"
                      onClick={() => { setEngrenagem(false);
                        setTrocaDePersona(true); }}>
                      <span className="ic" aria-hidden="true">◑</span>
                      Entrar como…
                    </button>
                  )}
                  <button type="button" role="menuitem"
                    className="engrenagem-item sair" onClick={sair}>
                    <span className="ic" aria-hidden="true">⏻</span>Sair
                  </button>
                </div>
              )}
            </div>
          )}
        </header>

      {/* A faixa da rede, só no celular e só quando é preciso (acima). Presa
          logo abaixo do topo, para acompanhar a rolagem. O ícone «sem rede» só
          quando a rede caiu: reenviando, ela ainda está lá. */}
      {comFaixaDaRede && (
        <div className="faixa-rede" role="status" ref={faixaRef}>
          {conexao === 'sem_rede' && <Icone nome="semrede" />}
          <span className="faixa-rede-texto">{textoDaFaixa}</span>
          {naFila > 0 && (
            <button type="button" className="faixa-rede-ver"
              aria-label="Ver os lançamentos guardados"
              onClick={() => setVerFilaAberta(true)}>
              Ver
            </button>
          )}
        </div>
      )}

      <div className="app-corpo">
      <aside className={`lateral${menuAberto ? ' aberta' : ''}${trilho ? ' trilho' : ''}`} id="lateral">
        {/* 266: recolher para o trilho / fixar aberto (só no desktop; no
            celular o menu é a gaveta de sempre). */}
        <button type="button" className="menu-trilho"
          aria-pressed={!trilho}
          title={trilho ? 'Fixar o menu aberto' : 'Recolher o menu para o trilho'}
          onClick={() => setTrilho((v) => { gravarTrilho(!v); return !v; })}>
          <span className="ic" aria-hidden="true">{trilho ? '»' : '«'}</span>
          <span className="menu-txt">{trilho ? 'Fixar o menu aberto' : 'Recolher o menu'}</span>
        </button>

        {/*
          A troca de espaço no tablet (onda 293, spec 03 §3.2). A faixa
          esconde os espaços abaixo de 900 px — a busca precisa do lugar —, e
          sem esta porta quem usa o tablet ficaria preso no espaço em que
          entrou. São os MESMOS links da faixa (`linksDosEspacos`): tocar num
          espaço grava a escolha e leva à porta dele, e a gaveta fecha na
          própria escolha (`escolherEspaco`) — o endereço pode nem mudar, se a
          porta do espaço é a página em que a pessoa já está. Só abaixo de 900
          px (a folha de estilo a esconde acima), e só quando há mais de um
          espaço para escolher. No celular (até 640 px) o «Mais» abre a folha
          que sobe, e a troca mora no topo dela (onda 308).
        */}
        {naFaixa.length > 1 && (
          <nav className="espacos-gaveta" aria-label="Espaços">
            {linksDosEspacos}
          </nav>
        )}

        {/*
          O menu é GERADO do registro de módulos. Não existe uma segunda
          lista escrita à mão que possa discordar dele — que é como o
          sistema chegou a ter cinco módulos no banco e nenhum no menu.
        */}
        <nav aria-label="Seções do sistema">
          {(alcancaOEspaco ? menuPorGrupo(espaco) : []).map((g, gi) => {
            /*
             * O menu mostra o que a pessoa ALCANÇA (UX-015/UX-068). A
             * regra mora em `modulos.ts` (`alcanca`), porque a tela de
             * Cadastros desenha os cartões dela pela mesma regra. E, desde
             * a onda 293, só o que mora no espaço aberto: o menu do Hub não
             * tem a bancada, e o do LabManager não tem os cadastros.
             */
            g = { ...g, itens: g.itens.filter((m) => alcanca(m, permissoes)) };
            if (g.itens.length === 0) return null;
            const aqui = g.itens.some((m) => estaAqui(onde, m));
            /* A memória é por espaço: «Trabalho» existe no Hub e no
               LabManager, e fechar um não fecha o outro. */
            const chave = g.grupo ? chaveDoGrupo(espaco, g.grupo) : '';
            const aberto = !g.grupo || aqui || !fechados.has(chave);
            const idLista = `menu-grupo-${gi}`;
            return (
              <div key={g.grupo ?? `g${gi}`} className="menu-bloco">
                {g.grupo && (
                  /*
                   * Botão, e não `<summary>` dentro de `<details>`.
                   *
                   * O `<details>` nativo guarda o próprio estado, e é
                   * justamente o que atrapalha aqui: o grupo da tela
                   * corrente tem que abrir mesmo que a pessoa o tenha
                   * fechado antes, e o nativo não deixa React mandar
                   * nisso sem brigar com ele a cada navegação.
                   */
                  <button
                    type="button"
                    className={`menu-grupo ${aberto ? 'aberto' : ''}`}
                    aria-expanded={aberto}
                    aria-controls={idLista}
                    onClick={() => alternarGrupo(chave)}
                  >
                    <span className="seta" aria-hidden="true">▸</span>
                    {g.grupo}
                    {/* Sem o número, um grupo fechado esconde quantos
                        itens ele guarda — e fica indistinguível de um
                        grupo vazio. */}
                    <em className="quantos">{g.itens.length}</em>
                  </button>
                )}
                <div id={idLista} hidden={!aberto}>
                  {g.itens.map((m) => (m.rota ? (
                    <Link
                      key={m.chave}
                      to={enderecoDoModulo(m)}
                      className={`menu-item ${estaAqui(onde, m) ? 'aqui' : ''}`}
                      aria-current={estaAqui(onde, m) ? 'page' : undefined}
                      title={trilho ? m.titulo : undefined}
                    >
                      {/* D2 da onda 321 (P-UI-43): o ícone desenhado do módulo, o mesmo da barra do celular. */}
                      <span className="ic" aria-hidden="true"><Icone nome={iconeDoModulo(m.chave)} /></span>{m.titulo}
                    </Link>
                  ) : (
                    <span key={m.chave} className="menu-item futuro" aria-disabled="true">
                      <span className="ic" aria-hidden="true"><Icone nome={iconeDoModulo(m.chave)} /></span>{m.titulo}
                      <em className="etiqueta">{ETIQUETA[m.situacao]}</em>
                    </span>
                  )))}
                </div>
              </div>
            );
          })}
        </nav>
      </aside>

      {menuAberto && (
        <button type="button" className="veu" aria-label="Fechar menu"
          onClick={() => setMenuAberto(false)} />
      )}

      {trocaDePersona && (
        <EscolherPersona fechar={() => setTrocaDePersona(false)} />
      )}

      <main className="corpo">
        {/*
          A faixa vem ANTES do cabeçalho, e atravessa a largura toda.
          Ela só existe na sessão emprestada — e aí precisa ser a
          primeira coisa que a pessoa vê ao olhar para a tela.
        */}

        <div className={`conteudo${TELA_INTEIRA.has(onde) || TELA_DA_OS.test(onde) || TELA_DA_CAMPANHA.test(onde) || TELA_DA_CAMPANHA_SPT.test(onde) || TELA_DO_COMERCIAL.test(onde) || TELA_DE_LISTA.has(onde) ? ' largo' : TELA_LARGA.has(onde) ? ' larga' : ''}`}>
          {/*
            * O caminho até aqui (onda 188): módulo › registro › tela. Vem
            * ANTES do conteúdo e dentro da coluna de leitura, alinhado ao
            * título — é a primeira linha que a pessoa lê, e responde
            * «onde estou» antes de o título responder «o que é isto».
            */}
          {/*
            * A recusa entra no lugar da tela (H1, onda 295), e sem a trilha:
            * a trilha de uma tela recusada apontaria para dentro dela — e, no
            * Campo da OS, buscaria a OS na API só para desenhar um caminho
            * que a pessoa não pode seguir.
            */}
          {recusa ? (
            recusa.tipo === 'conta'
              ? <ContaSemAcesso />
              : <RecusaDoEspaco espaco={recusa.espaco} voltar={recusa.voltar} />
          ) : (
            <>
              <Caminho onde={onde} declarados={caminhoDeclarado} />
              <ProvedorDoCaminho value={contextoDoCaminho}>
                {children}
              </ProvedorDoCaminho>
            </>
          )}
        </div>

        {/*
          Fora do `.conteudo` de propósito: o botão flutua sobre a tela
          inteira e acompanha o rolamento. Um canal de retorno que só
          aparece numa página é um canal que não é usado justamente na
          tela onde o defeito apareceu. No celular (onda 308, D4) o botão
          some e o item «Relatar um problema nesta tela» da folha «Mais» abre
          o mesmo diálogo (`abrirRelatar`).
        */}
        <Relatar />

        {/*
          * A barra inferior, só no celular (a folha a esconde acima de
          * 640 px). Fica FORA do `.conteudo` porque é navegação da
          * aplicação, e não conteúdo da tela — e porque precisa
          * atravessar a largura inteira, sem a coluna de leitura. Desde a
          * onda 293 é a barra do ESPAÇO aberto (`ATALHOS`).
          */}
        <nav className="barra-inferior" aria-label="Acesso rápido">
          {atalhos.map(({ modulo, curto, longo }) => {
            /*
             * O ícone é desenho próprio (RN-251 f): o sinal do menu do
             * desktop (♨, ≡…) o celular troca por emoji. Na Estufa, o selo
             * com as cápsulas esperando a pesagem seca — desenho só; quem
             * diz o número ao leitor de tela é o rótulo do link (spec 06
             * §5.2), com o plural da casa: «1 cápsula», «40 cápsulas» (R43).
             */
            const n = modulo.chave === 'estufa' && naEstufa !== null && naEstufa > 0 ? naEstufa : null;
            return (
              <Link
                key={modulo.chave}
                to={enderecoDoModulo(modulo)}
                aria-current={estaAqui(onde, modulo) ? 'page' : undefined}
                aria-label={n !== null
                  ? `Estufa, ${plural(n, 'cápsula', 'cápsulas')} esperando a pesagem seca`
                  : longo}
              >
                <span className="ic" aria-hidden="true">
                  <Icone nome={iconeDoModulo(modulo.chave)} />
                  {n !== null && (
                    <span className="selo-barra" aria-hidden="true">{n > 99 ? '99+' : n}</span>
                  )}
                </span>
                <span className="txt">{curto}</span>
              </Link>
            );
          })}
          {/*
            * «Mais» abria a MESMA gaveta do ☰ do cabeçalho — que a folha
            * de estilo esconde no celular, para não haver dois botões com o
            * mesmo efeito na mesma tela. Desde a onda 308, no celular (até
            * 640 px) ele abre a folha que sobe de baixo (D2, spec 06): o
            * polegar alcança o pé da tela, e não o canto de cima. Entre 641
            * e 900 px a barra nem aparece, e o botão continuaria valendo
            * para a gaveta.
            */}
          <button
            type="button"
            aria-expanded={estreito ? folhaAberta : menuAberto}
            /* A folha fechada não está no documento: só aponta para ela
               aberta (um id que não existe não controla nada). */
            aria-controls={estreito ? (folhaAberta ? 'folha-mais' : undefined) : 'lateral'}
            onClick={() => (estreito ? setFolhaAberta((v) => !v) : setMenuAberto((v) => !v))}
          >
            <span className="ic" aria-hidden="true"><Icone nome="mais" /></span>
            <span className="txt">Mais</span>
          </button>
        </nav>

        {/* A folha «Mais» (onda 308): os espaços, os módulos fora da barra
            e a conta. Sobe no `body`, por portal, e fechada não desenha
            nada. A troca de espaço é a mesma lista da faixa e da gaveta. */}
        <FolhaDoMais
          aberta={folhaAberta}
          aoFechar={() => setFolhaAberta(false)}
          espacos={naFaixa.length > 1 ? linksDosEspacos : null}
          grupos={gruposDaFolha}
          tema={tema}
          aoTrocarTema={() => setTema((t) => proximoTema(t))}
          aoEntrarComo={entrarComoDaFolha}
        />

        {verFilaAberta && (
          <FilaDeEnvio aoFechar={() => setVerFilaAberta(false)} />
        )}
      </main>
      </div>
    </div>
  );
}
